import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Heart, Target, Eye, Ban, UserCheck, Receipt, Scale } from "lucide-react";

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/donation-policy`;
const defaultImage = `${siteUrl}/og-blogs.jpg`;
const logoImage = `${siteUrl}/logo512.png`;

const pageTitle = "Donation Policy | SEVAA Foundation";
const pageDescription =
  "Read SEVAA Foundation's donation policy covering voluntary contributions, non-refundable donations, receipts, transparency and ethical use of funds.";

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${pageUrl}#webpage`,
  name: pageTitle,
  url: pageUrl,
  inLanguage: "en-IN",
  description: pageDescription,
  isAccessibleForFree: true,
  isPartOf: {
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    name: "SEVAA Foundation",
    url: siteUrl,
  },
  publisher: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
    logo: {
      "@type": "ImageObject",
      url: logoImage,
    },
  },
  about: [
    "Donation Policy",
    "Donor Transparency",
    "Donation Receipts",
    "Non Refundable Donations",
    "Ethical Use of Funds"
  ],
  primaryImageOfPage: {
    "@type": "ImageObject",
    url: defaultImage,
    width: 1200,
    height: 630,
  },
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "@id": `${pageUrl}#breadcrumb`,
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${siteUrl}/`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Donation Policy",
      item: pageUrl,
    },
  ],
};


const DonationPolicy = () => {
  // Data array for the donation policy content
  const donationData = [
    {
      id: 1,
      title: "Purpose of Donations",
      icon: <Target className="w-6 h-6 text-teal-600" />,
      content: "All donations are used to support charitable programs, operational needs, and outreach activities aligned with our mission."
    },
    {
      id: 2,
      title: "Transparency",
      icon: <Eye className="w-6 h-6 text-teal-600" />,
      content: "We aim to use donations responsibly and ethically. Funds may be allocated based on priority needs, emergency situations, and program sustainability."
    },
    {
      id: 3,
      title: "Non-Refundable Donations",
      icon: <Ban className="w-6 h-6 text-rose-500" />,
      content: "All donations are voluntary and non-refundable once processed."
    },
    {
      id: 4,
      title: "Donor Responsibility",
      icon: <UserCheck className="w-6 h-6 text-teal-600" />,
      content: "Donors are requested to verify donation details carefully before completing transactions."
    },
    {
      id: 5,
      title: "Receipts and Acknowledgement",
      icon: <Receipt className="w-6 h-6 text-teal-600" />,
      content: "Donation receipts or acknowledgements may be issued where applicable and subject to regulatory requirements."
    },
    {
      id: 6,
      title: "Ethical Use of Funds",
      icon: <Scale className="w-6 h-6 text-teal-600" />,
      content: "SEVAA does not support any activity that violates laws, human dignity, or social welfare objectives."
    }
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="title" content={pageTitle} />
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="SEVAA Foundation" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="language" content="English, Hindi" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SEVAA Foundation" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={defaultImage} />
        <meta property="og:image:alt" content="SEVAA Foundation donation policy and donor transparency" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={defaultImage} />
        <meta name="twitter:image:alt" content="SEVAA Foundation donation policy and donor transparency" />

        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

    <div className="bg-slate-50 min-h-screen font-poppins pb-20">
      
      {/* 1. HERO SECTION */}
      <div className="relative w-full h-[400px] bg-teal-900 overflow-hidden">
        {/* Abstract Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 -left-40 w-96 h-96 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl opacity-70"></div>
          <div className="absolute top-0 -right-40 w-96 h-96 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl opacity-70"></div>
          <div className="absolute -bottom-40 left-20 w-96 h-96 bg-teal-300 rounded-full mix-blend-multiply filter blur-3xl opacity-70"></div>
        </div>

        {/* Hero Content */}
        <div className="relative h-full max-w-4xl mx-auto px-6 flex flex-col items-center justify-center text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="w-16 h-16 bg-teal-800 rounded-2xl border border-teal-700 flex items-center justify-center mb-6 shadow-xl"
          >
            <Heart className="w-8 h-8 text-orange-400" />
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight"
          >
            Donation Policy
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-teal-100 text-base md:text-lg max-w-2xl font-medium"
          >
            SEVAA welcomes voluntary contributions to support our work in education, healthcare, food assistance, and social upliftment.
          </motion.p>
        </div>
      </div>

      {/* 2. CONTENT SECTION */}
      <div className="max-w-7xl mx-auto px-6 -mt-10 relative z-10">
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 p-8 md:p-12">
          
          <p className="text-slate-500 mb-10 text-sm font-medium border-b border-slate-100 pb-6 leading-relaxed">
            Your contributions fuel our mission to bring positive change. We ensure complete transparency and accountability in how every single donation is utilized.
          </p>

          <div className="space-y-12">
            {donationData.map((policy, index) => (
              <motion.div 
                key={policy.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="flex flex-col md:flex-row gap-4 md:gap-6 group"
              >
                {/* Icon Column */}
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center group-hover:bg-teal-600 group-hover:text-white transition-colors duration-300">
                    {React.cloneElement(policy.icon, { className: "w-6 h-6 transition-colors duration-300 group-hover:text-white" })}
                  </div>
                </div>

                {/* Text Column */}
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <span className="text-slate-300 font-black text-lg">{policy.id}.</span> 
                    {policy.title}
                  </h2>
                  <p className="text-slate-600 leading-relaxed font-medium">
                    {policy.content}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

        </div>
      </div>

      </div>
    </>
  );
};

export default DonationPolicy;
